import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useApolloClient } from '@apollo/client';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { downloadExcelFile } from '@utils/excel/download-json-excel-service';

import {
  ALL,
  buildExportProposalsDrawerSections,
  exportProposalsExcelColumnWidths,
  exportProposalsExcelHeaderGroups,
  exportProposalsExcelHeaders,
  exportProposalsExcelStyles
} from '@common/constants';
import { searchProposalsForExport } from '@common/services';
import {
  useGetExportProposalsLOVs,
  useSearchUsersForExport
} from '@common/slices';
import { useDrawerSubmission } from '@common/hooks';

import DateService from 'src/core/services/DateService';
import FormattingService from '@core/services/FormattingService';
import { IExportProposalsFilters, IBaseDrawerProps } from '@common/types';

// Helper functions
const buildExportProposalsLovs = (lovData: any) => {
  const statusEnum =
    lovData?.ProposalStatusList?.enumValues?.map((e: any) => e.name) || [];
  const businessPartners =
    lovData?.production?.getProposalBusinessPartners || [];
  const currencies = lovData?.core?.getCurrencies || [];

  const Status: Record<string, string> = { [ALL]: 'All' };
  statusEnum.forEach((v: string) => {
    Status[v] = FormattingService.formatEnumToDisplay(v) ?? v;
  });

  const BusinessPartner: Record<string, string> = { [ALL]: 'All' };
  businessPartners.forEach((bp: any) => {
    if (bp?.id) {
      BusinessPartner[String(bp.id)] = `${bp.code} - ${bp.fullName}`;
    }
  });

  const Currency: Record<string, string> = { [ALL]: 'All' };
  currencies.forEach((c: any) => {
    if (c?.id) {
      Currency[String(c.id)] = String(c.title ?? c.code);
    }
  });

  return { Status, BusinessPartner, Currency };
};

const normalizeExportProposalsMulti = (v: any): string[] => {
  if (!v) return [];
  return Array.isArray(v) ? v : [v];
};

const fetchAllProposalsForExport = async (
  client: any,
  query: any,
  vars: {
    selectedUsers: string[] | null;
    selectedBusinessPartners: string[] | null;
    effectiveOnStart: any | null;
    effectiveOnEnd: any | null;
    expiryOnStart: any | null;
    expiryOnEnd: any | null;
    issueOnStart: any | null;
    issueOnEnd: any | null;
    createdOnStart: any | null;
    createdOnEnd: any | null;
  }
) => {
  const pageSize = 500;
  let currentPage = 1;
  const all: any[] = [];
  let hasMore = true;

  while (hasMore) {
    const { data } = await client.query({
      query,
      fetchPolicy: 'no-cache',
      variables: {
        currentPage,
        currentPageSize: pageSize,
        Attribute: 'createdOn',
        Descending: true,
        ...vars
      }
    });

    const items = data?.production?.searchProposals?.items || [];
    all.push(...items);

    hasMore = items.length === pageSize;
    currentPage += 1;
  }

  return all;
};

const yesNo = (v: any) => {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return '';
};

const mapProposalsToExcelData = (items: any[]) =>
  items.map((p: any) => ({
    name: p?.name ?? '',
    plan: p?.plan ? `${p.plan.code} - ${p.plan.name}` : '',
    user: p?.user
      ? `${p.user.code} - ${[p.user.firstName, p.user.middleName, p.user.lastName].filter(Boolean).join(' ')}`
      : '',
    broker: p?.mainBusinessPartner
      ? `${p.mainBusinessPartner.code} - ${p.mainBusinessPartner.fullName}`
      : '',
    policyEffectiveDate: p?.policyEffectiveDate ?? '',
    policyExpiryDate: p?.policyExpiryDate ?? '',
    policyIssueDate: p?.policyIssueDate ?? '',
    currency: p?.policyCurrency?.title ?? '',
    isRenewal: yesNo(p?.isRenewal),
    renewalFrom: p?.renewalFrom?.policyNumber ?? p?.renewalFrom?.name ?? '',
    status: FormattingService.formatEnumToDisplay(p?.status) ?? '',
    statusReason: p?.statusReason ?? ''
  }));


const ExportProposalsDrawer: React.FC<IBaseDrawerProps> = ({ onSuccess }) => {
  const client = useApolloClient();
  const { submitButtonState, formDisabled, handleSubmission } =
    useDrawerSubmission();

  const [values, setValues] = useState<IExportProposalsFilters>({
    user: [ALL],
    businessPartner: [ALL],
    currency: [ALL],
    status: [ALL],
    policyEffectiveDateRange: [],
    policyExpiryDateRange: [],
    policyIssueDateRange: [],
    dateRange: []
  });

  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [sections, setSections] = useState<IDynamicFormWidgetSection[]>([]);

  const { data: lovData } = useGetExportProposalsLOVs.useQuery();
  const [searchUsers] = useSearchUsersForExport.useLazy();

  useEffect(() => {
    const built = buildExportProposalsLovs(lovData);
    setLovs(built);
    setSections(buildExportProposalsDrawerSections(values, built));
  }, [lovData]);

  const performSubmission = async (): Promise<void> => {
    const [data] = normalizeDynamicFormDrawerValues(sections, values);
    const selectedUsers = normalizeExportProposalsMulti(data.user);
    const selectedBusinessPartners = normalizeExportProposalsMulti(
      data.businessPartner
    );
    const selectedStatuses = normalizeExportProposalsMulti(data.status);
    const selectedCurrencies = normalizeExportProposalsMulti(data.currency);

    const formatDateRange = (range: any[]) => {
      if (!range?.[0] || !range?.[1]) return null;
      return [
        DateService.formatDateTimeBackend(range[0]),
        DateService.formatDateTimeBackend(range[1])
      ];
    };

    const effectiveDateRange = formatDateRange(data.policyEffectiveDateRange);
    const expiryDateRange = formatDateRange(data.policyExpiryDateRange);
    const issueDateRange = formatDateRange(data.policyIssueDateRange);
    const createdOnRange = formatDateRange(data.dateRange);

    const allItems = await fetchAllProposalsForExport(
      client,
      searchProposalsForExport,
      {
        selectedUsers: selectedUsers.includes(ALL) ? null : selectedUsers,
        selectedBusinessPartners: selectedBusinessPartners.includes(ALL)
          ? null
          : selectedBusinessPartners,
        effectiveOnStart: effectiveDateRange?.[0] || null,
        effectiveOnEnd: effectiveDateRange?.[1] || null,
        expiryOnStart: expiryDateRange?.[0] || null,
        expiryOnEnd: expiryDateRange?.[1] || null,
        issueOnStart: issueDateRange?.[0] || null,
        issueOnEnd: issueDateRange?.[1] || null,
        createdOnStart: createdOnRange?.[0] || null,
        createdOnEnd: createdOnRange?.[1] || null
      }
    );

    const filterByStatuses = !selectedStatuses.includes(ALL);
    const filterByCurrencies = !selectedCurrencies.includes(ALL);

    const items = allItems.filter((p: any) => {
      if (filterByStatuses && !selectedStatuses.includes(p?.status))
        return false;
      if (
        filterByCurrencies &&
        !selectedCurrencies.includes(p?.policyCurrency?.id)
      )
        return false;

      return true;
    });

    if (!items.length) {
      toast.error(
        <ToastErrorMessage>
          No proposals found for the selected filters
        </ToastErrorMessage>
      );
      return;
    }

    const excelData = mapProposalsToExcelData(items);

    downloadExcelFile({
      data: excelData,
      filename: 'Redbridge_Proposals',
      headers: exportProposalsExcelHeaders,
      sheetName: 'Sheet1',
      groupHeaders: exportProposalsExcelHeaderGroups,
      styles: exportProposalsExcelStyles,
      columnWidths: exportProposalsExcelColumnWidths,
      freezeHeaderRows: 2,
      headerRowHeight: 14,
      groupHeaderRowHeight: 18,
      separator: { enabled: true, applyTo: 'headerOnly', style: 'medium' }
    });
    toast.success(
      <ToastSuccessMessage>Proposals successfully exported</ToastSuccessMessage>
    );
  };

  const submitForm = () => {
    handleSubmission(
      performSubmission,
      () => onSuccess?.(),
      (error) => {
        console.error('Export error:', error);
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      }
    );
  };

  const handleChange = (
    fieldName: keyof IExportProposalsFilters,
    value: any,
    _sectionIndex: number,
    updatedValues: IExportProposalsFilters
  ) => {
    const newValues = { ...updatedValues, [fieldName]: value };
    setValues(newValues);
    setSections(buildExportProposalsDrawerSections(newValues, lovs));
  };

  const handleAutocompleteSearch = async (
    fieldName: string,
    query: string,
    pagination: { pageNumber: number; pageSize: number }
  ) => {
    if (fieldName === 'user') {
      const result = await searchUsers({
        variables: {
          keyword: query || '',
          pageNumber: pagination.pageNumber,
          pageSize: pagination.pageSize
        },
        fetchPolicy: 'no-cache'
      });
      const items = result?.data?.core?.searchUsers?.items ?? [];
      const options: Record<string, string> = { [ALL]: 'All' };
      items.forEach((u: any) => {
        if (u?.id) {
          const fullName = [u.firstName, u.middleName, u.lastName]
            .filter(Boolean)
            .join(' ');
          options[String(u.id)] = `${u.code ?? ''} - ${fullName}`;
        }
      });
      return { options, totalCount: items.length + 1 };
    }

    const allOptions = lovs[fieldName] ?? {};
    const filtered = Object.fromEntries(
      Object.entries(allOptions).filter(([, label]) =>
        label.toLowerCase().includes(query.toLowerCase())
      )
    );
    const entries = Object.entries(filtered);
    const start = (pagination.pageNumber - 1) * pagination.pageSize;

    return {
      options: Object.fromEntries(
        entries.slice(start, start + pagination.pageSize)
      ),
      totalCount: entries.length
    };
  };

  const handleInitialMultiAutocompleteSearch = async (
    fieldName: string,
    values: string[]
  ) => {
    if (fieldName === 'user') {
      const options: Record<string, string> = {};
      if (values.includes(ALL)) options[ALL] = 'All';

      return { options, totalCount: Object.keys(options).length };
    }

    const allOptions = lovs[fieldName] ?? {};
    const options = Object.fromEntries(
      values.filter((v) => allOptions[v]).map((v) => [v, allOptions[v]])
    );

    return { options, totalCount: Object.keys(options).length };
  };

  return (
    <DynamicFormWidget
      onChange={handleChange}
      sections={sections}
      disableForm={formDisabled}
      submitButtonState={submitButtonState}
      onSubmit={submitForm}
      onAutocompleteSearch={handleAutocompleteSearch}
      initialMultiAutocompleteSearch={handleInitialMultiAutocompleteSearch}
    />
  );
};

export { ExportProposalsDrawer };
